import type { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { env } from '@config/env';
import { useAuthStore } from '../stores/authStore';
import { getJoinTenantContext } from './joinTenantContext';
import { toApiError } from './errors';

/**
 * Refresh-token coordination — at most one in-flight refresh; concurrent 401s
 * await the same promise then retry their original request with the new token.
 */
type RefreshFn = (refreshToken: string) => Promise<{ accessToken: string; refreshToken?: string }>;

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Inject the standard headers every API call needs:
 *   - `Authorization: Bearer <accessToken>` (when authenticated)
 *   - `x-tenant-id`   — the tenant this call is addressed to (regional API only; ignored by
 *                       Core). A caller working inside another community sets an explicit
 *                       override, which wins: the active session's tenant is rewritten by any
 *                       domain resolution, so it cannot be trusted to still name the tenant the
 *                       request belongs to.
 */
function attachAuthHeaders(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const auth = useAuthStore.getState();

  // Only pin a template when the build configured one; otherwise let the backend
  // resolve the tenant's effective member template (avoids forcing an empty/invalid pin).
  if (env.appTemplateId) {
    config.headers.set('x-app-template-id', env.appTemplateId);
  }

  const token = auth.accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  // A call that names its own tenant wins. Signup pins itself to the community whose member
  // types it listed, and the ambient store is not stable enough to override it — every
  // /resolve-domain rewrites it, which silently moved signup to the wrong community mid-form.
  if (!config.headers.has('x-tenant-id')) {
    const tenantId = getJoinTenantContext() ?? auth.tenantId;
    if (tenantId) {
      config.headers.set('x-tenant-id', tenantId);
    }
  }

  return config;
}

interface RegisterRefreshOptions {
  /** Implementation of POST /auth/refresh — supplied by the api module that owns it. */
  refresh: RefreshFn;
  /** Called when the session ends (refresh refused, or nothing to refresh) — typically routes the user to login. */
  onRefreshFailed?: () => void;
}

/**
 * Renew the access token now, from the session's refresh token. A cold start and a biometric
 * unlock call this before the app renders; a 401 does the same through the interceptor. Shares
 * any renewal already in flight. Null when there is no new token: nothing to renew with, the
 * refresh was refused (the session is over), or the server could not be reached.
 */
export function renewAccessToken(options: RegisterRefreshOptions): Promise<string | null> {
  const { refreshToken } = useAuthStore.getState();
  return refreshToken ? coordinatedRefresh(options, refreshToken) : Promise.resolve(null);
}

/**
 * Wire the standard request + response interceptors onto an axios client.
 * Both the Core API and Tenant (regional) API clients call this — the only
 * difference between them is which `refresh` they pass in.
 */
export function installInterceptors(client: AxiosInstance, options: RegisterRefreshOptions): void {
  client.interceptors.request.use(attachAuthHeaders, (error) => Promise.reject(error));

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
      const status = error.response?.status;

      const isAuthEndpoint =
        original?.url?.includes('/auth/login') ||
        original?.url?.includes('/portal-auth/login') ||
        original?.url?.includes('/auth/refresh');

      // A 401 means "renew the session" only when there is a refresh token to renew it with.
      const refreshToken = useAuthStore.getState().refreshToken;

      if (status === 401 && original && !original._retried && !isAuthEndpoint && refreshToken) {
        original._retried = true;

        const newToken = await coordinatedRefresh(options, refreshToken);
        if (newToken) {
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
          return client.request(original);
        }
      }

      // A 401 on a call that carried the current access token, with no refresh token to renew
      // it, is a session that can never recover (its refresh token was cleared or lost): end it,
      // or every call fails until the member signs out by hand. A call without the token is
      // just a rejected request.
      const accessToken = useAuthStore.getState().accessToken;
      const sentToken = String(original?.headers?.Authorization ?? '') === `Bearer ${accessToken}`;
      if (status === 401 && !isAuthEndpoint && !refreshToken && accessToken && sentToken) {
        options.onRefreshFailed?.();
      }

      return Promise.reject(toApiError(error));
    },
  );
}

function coordinatedRefresh(options: RegisterRefreshOptions, refreshToken: string): Promise<string | null> {
  inflightRefresh ??= (async () => {
    // 1. Exchange the refresh token. The server refusing it (4xx) ends the session, once however
    //    many requests wait on it. An unreachable server keeps the session, so an app started
    //    offline renews on a later call.
    let result: Awaited<ReturnType<RefreshFn>>;
    try {
      result = await options.refresh(refreshToken);
    } catch (error) {
      const { status } = toApiError(error);
      if (status >= 400 && status < 500) options.onRefreshFailed?.();
      return null;
    }

    // 2. `setTokens` rather than `setAuth`: refresh endpoints return tokens only, and the cached
    //    user stays as-is.
    await useAuthStore.getState().setTokens(result.accessToken, result.refreshToken);
    return result.accessToken;
  })().finally(() => {
    inflightRefresh = null;
  });

  return inflightRefresh;
}
